import type { CSSProperties, ReactElement, ReactNode } from "react";
import { palette, radius, space } from "@ux/tokens";
import { useViewport } from "@ux/hooks";

export interface SplitPaneProps {
    readonly master: ReactNode;
    readonly detail: ReactNode;
    readonly masterMin?: number;
    readonly masterPreferred?: number;
    /**
     * Below the desktop breakpoint the SplitPane stops splitting and
     * shows exactly one panel — controlled by this prop. Pages typically
     * derive it from their own selection state ("idle" → master, anything
     * else → detail) so picking a row on phone or tablet pushes the form
     * to fill the whole content area, and Cancel returns to the list.
     *
     * Defaults to "master" so a SplitPane that's never been told otherwise
     * still does something sensible on a narrow viewport.
     */
    readonly activeBelowDesktop?: "master" | "detail";
}

const paneStyle: CSSProperties = {
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: radius.lg,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
};

export function SplitPane({
    master,
    detail,
    masterMin = 320,
    masterPreferred = 420,
    activeBelowDesktop = "master",
}: SplitPaneProps): ReactElement {
    const { isDesktop } = useViewport();

    if (!isDesktop) {
        // Single-pane mode: render the active panel filling the
        // available space. Padding is reduced to claw back room on
        // narrow viewports where every pixel matters.
        const singleStyle: CSSProperties = {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: space[3],
        };
        return (
            <div style={singleStyle}>
                <section
                    style={paneStyle}
                    aria-label={activeBelowDesktop === "detail" ? "Detail" : "Master list"}
                >
                    {activeBelowDesktop === "detail" ? detail : master}
                </section>
            </div>
        );
    }

    const splitStyle: CSSProperties = {
        flex: 1,
        minHeight: 0,
        display: "grid",
        gap: space[4],
        padding: space[4],
        gridTemplateColumns: `minmax(${String(masterMin)}px, ${String(masterPreferred)}px) 1fr`,
        maxWidth: "100%",
    };
    return (
        <div style={splitStyle}>
            <section style={paneStyle} aria-label="Master list">
                {master}
            </section>
            <section style={paneStyle} aria-label="Detail">
                {detail}
            </section>
        </div>
    );
}
